import { isInApp } from "@/lib/links/in-app";

/* A call opens in its own tab, as Slack's do, so the work tab stays free. Only the tab that opened
   it can reach it by name; any other tab asks it over the call channel (`call-channel.ts`). */
export const CALL_TAB_NAME = "people-ai-call";
const PREFIX = "/call/";
/** How long a tab just opened counts as the call's, before it can say so itself. */
const IN_FLIGHT_MS = 8_000;

export interface CallTabOpening {
  conversationId: string;
  callId?: string;
  meetingId?: string | null;
  /** Rides the channel, never the address: names stay out of history and logs. */
  title?: string;
  answer?: { video: boolean };
}

type CallTabSearch = {
  callId?: string;
  meetingId?: string;
  answer?: "video" | "audio";
};

/** This tab is a call's own tab. */
export const isCallTab = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith(PREFIX);

/** In a call's own tab: the call its address asks for, read before anything else loads. */
export function callTabTarget(): { conversationId: string; callId?: string } | null {
  if (!isCallTab()) return null;
  const conversationId = decodeURIComponent(window.location.pathname.slice(PREFIX.length));
  const callId = new URLSearchParams(window.location.search).get("callId");
  return conversationId ? { conversationId, ...(callId ? { callId } : {}) } : null;
}

export function callTabSearch(opening: CallTabOpening): CallTabSearch {
  return {
    ...(opening.callId ? { callId: opening.callId } : {}),
    ...(opening.meetingId ? { meetingId: opening.meetingId } : {}),
    ...(opening.answer ? { answer: opening.answer.video ? "video" : "audio" } : {}),
  };
}

function callTabPath(opening: CallTabOpening): string {
  const query = new URLSearchParams(callTabSearch(opening)).toString();
  return `${PREFIX}${encodeURIComponent(opening.conversationId)}${query ? `?${query}` : ""}`;
}

export function readCallTabSearch(search: Record<string, unknown>): CallTabSearch {
  const text = (key: string) => (typeof search[key] === "string" ? search[key] : undefined);
  const answer = text("answer");
  return {
    callId: text("callId"),
    meetingId: text("meetingId"),
    answer: answer === "video" || answer === "audio" ? answer : undefined,
  };
}

let opened: { tab: Window; opening: CallTabOpening; at: number } | null = null;

/** Opens the call's tab. Must run in the click itself, before any await, or the browser blocks
 * it. False where a tab cannot open (the phone app's web view, a blocked pop-up): the call then
 * opens here, as before. */
export function openCallTab(opening: CallTabOpening): boolean {
  if (isInApp() || isCallTab()) return false;
  const tab = window.open(callTabPath(opening), CALL_TAB_NAME);
  if (!tab) return false;
  opened = { tab, opening, at: Date.now() };
  return true;
}

/** The call this tab opened a moment ago, while that tab is still loading. */
export function openingInFlight(): CallTabOpening | null {
  if (!opened || opened.tab.closed || Date.now() - opened.at > IN_FLIGHT_MS) return null;
  return opened.opening;
}

/** Brings forward the call tab this tab opened. False when this tab never opened it or it has
 * closed: a tab cannot be reached by name from another, and asking would open a blank one. */
export function focusCallTab(): boolean {
  if (!opened || opened.tab.closed) return false;
  opened.tab.focus();
  return true;
}
